/**
 * Segmentar y Grupos en palabras: las etapas que se ven mientras se arman los grupos, el estado de cada campaña y los
 * números del reporte con su formato. Puro y testeado (`segmentar.test.ts`).
 *
 * Los textos del reporte son los mismos que imprime el PDF (backend, segmentaciones/util/reporte-html.util.ts): la
 * pantalla y el archivo cuentan lo mismo con las mismas palabras.
 */

import { num } from "../../campana-nueva/utils/format";
import { estadoEnPalabras } from "../../envios/logic/resultado";
import { rutaMensajes } from "../../mensajes/logic/mensajes";
import type { Etapa, Reporte, Segmentacion } from "../types";

export const R_SEGMENTACIONES = "/activa/segmentaciones";
export const R_NUEVA_CAMPANA = `${R_SEGMENTACIONES}/nueva`;
/** «Con los grupos de tu Público» (01/11/2026): estática, le gana a `/activa/segmentaciones/:id`. */
export const R_DESDE_PUBLICO = `${R_SEGMENTACIONES}/desde-publico`;
export const rutaSegmentar = (id: string) => `${R_SEGMENTACIONES}/${encodeURIComponent(id)}`;
export const rutaGrupos = (id: string) => `${R_SEGMENTACIONES}/${encodeURIComponent(id)}/grupos`;
export const rutaResultado = (id: string) => `${R_SEGMENTACIONES}/${encodeURIComponent(id)}/resultado`;

/** Cada cuánto se mira la campaña mientras se arman los grupos. */
export const CADA_MS_SEGMENTANDO = 2000;

/** El % de control que se puede elegir (DISENO, «Envío»: 10 / 15 / 20 / 30; el default es 15). */
export const OPCIONES_CONTROL = [10, 15, 20, 30];
export const CONTROL_POR_DEFECTO = 15;

/** La única frase con esa palabra que se muestra (DISENO, «Reglas para todo el producto»). */
export const FRASE_PROCESANDO = "EL ALGORITMO DEENEX ESTÁ PROCESANDO TU INFORMACIÓN";

export interface EtapaVisible {
  etapa: Etapa;
  texto: string;
  detalle: string;
  grande: boolean;
  estado: "hecha" | "ahora" | "falta";
}

const TEXTOS: { etapa: Etapa; texto: string; grande?: boolean }[] = [
  { etapa: "base", texto: "Leyendo tu base" },
  { etapa: "ipc", texto: "Llevando los montos a pesos de hoy" },
  { etapa: "comportamiento", texto: "Midiendo el comportamiento de cada cliente" },
  { etapa: "agrupando", texto: FRASE_PROCESANDO, grande: true },
  { etapa: "verificando", texto: "Verificando los grupos encontrados" },
];

/** Las etapas de ARQUITECTURA §3, con lo que ya se sabe de cada una («56.423 pedidos», «IPC INDEC · ago-2026»). */
export function etapasVisibles(s: Pick<Segmentacion, "etapas" | "detalle">): EtapaVisible[] {
  const estadoDe = new Map(s.etapas.map((e) => [e.etapa, e.estado]));
  const detalles: Partial<Record<Etapa, string>> = {
    base: s.detalle.pedidos != null ? `${num(s.detalle.pedidos)} ${s.detalle.pedidos === 1 ? "pedido" : "pedidos"}` : "",
    ipc: s.detalle.ipc ?? "",
    comportamiento: s.detalle.clientes != null ? `${num(s.detalle.clientes)} ${s.detalle.clientes === 1 ? "cliente" : "clientes"}` : "",
  };
  return TEXTOS.map((t) => ({ etapa: t.etapa, texto: t.texto, detalle: detalles[t.etapa] ?? "", grande: !!t.grande, estado: estadoDe.get(t.etapa) ?? "falta" }));
}

/** Cuánto va, de 0 a 100: las etapas hechas sobre las cinco que se ven. */
export function avance(s: Pick<Segmentacion, "etapas" | "estado">): number {
  if (s.estado !== "procesando") return 100;
  const hechas = s.etapas.filter((e) => e.estado === "hecha" && e.etapa !== "listo").length;
  return Math.round((hechas * 100) / TEXTOS.length);
}

export const ESTADOS: Record<Segmentacion["estado"], { texto: string; tono: "violeta" | "ambar" | "rojo" | "gris" }> = {
  procesando: { texto: "Armando los grupos", tono: "violeta" },
  nombres_pendientes: { texto: "Faltan los nombres", tono: "ambar" },
  borrador: { texto: "Borrador", tono: "gris" },
  fallida: { texto: "No se pudo", tono: "rojo" },
};

/**
 * En qué paso quedó una campaña y cómo se sigue (Inicio y Campañas). `paso` es el de «Nueva campaña»: 0 Segmentar,
 * 1 Grupos, 2 Mensajes, 3 Envío, y 4 el Resultado. Con los grupos listos se sigue en Mensajes; con los mensajes
 * guardados, el envío se prepara al pie de Mensajes; con el envío preparado, mientras no salió nada se sigue ahí, y
 * después se mira el Resultado («Midiendo hasta el 20/10», fase 5).
 */
export interface PasoDeCampana {
  paso: 0 | 1 | 2 | 3 | 4;
  texto: string;
  accion: string;
  destino: string;
  tono: "violeta" | "ambar" | "rojo" | "gris" | "verde";
}

export function pasoDeCampana(c: Pick<Segmentacion, "id" | "estado" | "mensajesGuardados" | "resultado">): PasoDeCampana {
  if (c.resultado) {
    if (c.resultado.estado === "preparada") return { paso: 3, texto: "Envío preparado · todavía no salió", accion: "Ver el envío", destino: rutaMensajes(c.id), tono: "violeta" };
    const e = estadoEnPalabras(c.resultado);
    return { paso: 4, texto: e.texto, accion: "Ver resultado", destino: rutaResultado(c.id), tono: e.tono };
  }
  if (c.estado === "procesando") return { paso: 0, texto: "Armando los grupos", accion: "Ver cómo va", destino: rutaSegmentar(c.id), tono: "violeta" };
  if (c.estado === "fallida") return { paso: 0, texto: "No se pudo segmentar", accion: "Ver qué pasó", destino: rutaSegmentar(c.id), tono: "rojo" };
  if (c.estado === "nombres_pendientes") return { paso: 1, texto: "Faltan los nombres de los grupos", accion: "Ponerles nombre", destino: rutaGrupos(c.id), tono: "ambar" };
  if ((c.mensajesGuardados ?? 0) > 0) return { paso: 3, texto: "Mensajes guardados · falta el envío", accion: "Preparar el envío", destino: rutaMensajes(c.id), tono: "violeta" };
  return { paso: 2, texto: "Grupos listos · faltan los mensajes", accion: "Escribir los mensajes", destino: rutaMensajes(c.id), tono: "violeta" };
}

/** Un grupo todavía sin nombre se nombra por su número: «Falta el nombre» se leía como un error. */
export const nombreDelGrupo = (g: { nombre: string | null }, i: number) => g.nombre ?? `Grupo ${i + 1}`;

/* ── Números ─────────────────────────────────────────────────────────────────────────────────── */

export const pesos = (n: number) => `$${num(n)}`;
export const dias = (n: number | null) => (n == null ? "—" : `${num(n)} ${n === 1 ? "día" : "días"}`);

/** «2026-09-21» → «21/09/2026». */
export function fecha(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "—";
}

/** Para el nombre de un archivo: sin acentos ni signos («Octubre · 1ª quincena» → «octubre-1a-quincena»). */
export function slug(s: string): string {
  return (
    String(s ?? "")
      .normalize("NFKD")
      .replace(/\p{M}/gu, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "campana"
  );
}

/** «El 70% compra con hábito y el 14%, de vez en cuando»; sin nadie de vez en cuando, sólo lo primero (como el PDF). */
export function textoHabito(pctConHabito: number, pctDeVezEnCuando: number): string {
  const habito = `El ${num(pctConHabito)}% compra con hábito`;
  return pctDeVezEnCuando > 0 ? `${habito} y el ${num(pctDeVezEnCuando)}%, de vez en cuando` : `${habito}.`;
}

/** «Qué encontramos»: el número grande y lo que dice (los mismos que el PDF). */
export function hechos(r: Reporte): { valor: string; texto: string }[] {
  const e = r.encontramos;
  return [
    { valor: `${num(e.pctSinComprar6Meses)}%`, texto: "no compra hace más de 6 meses" },
    { valor: `${num(e.pctUnaSolaCompra)}%`, texto: "compró una sola vez" },
    { valor: `8% → ${num(e.top8)}%`, texto: `el 8% de tus clientes trae el ${num(e.top8)}% de la facturación, y el 20% trae el ${num(e.top20)}%` },
    ...(e.ritmo != null
      ? [{ valor: dias(e.ritmo), texto: `quienes vuelven, vuelven cada ${dias(e.ritmo)}. ${textoHabito(e.pctConHabito, e.pctDeVezEnCuando)}` }]
      : []),
  ];
}

export const textoSeVan = (s: NonNullable<Reporte["seVan"]>) =>
  `Compraban cada ${dias(s.ritmo)} y llevan ${dias(s.sinComprar)} sin volver, más del doble de lo que solían tardar. El cliente típico gastó ${pesos(s.gasto)}.`;

/** El título del reporte: «Tus 17.362 clientes, en 4 grupos». */
export const tituloReporte = (r: Reporte) => `Tus ${num(r.compraron)} clientes, en ${r.grupos.length} ${r.grupos.length === 1 ? "grupo" : "grupos"}`;

/** Los grupos que todavía no tienen nombre comercial. */
export const sinNombre = (s: Pick<Segmentacion, "grupos">) => s.grupos.filter((g) => !g.nombre);
